import React, { useState } from 'react';
import { View, ScrollView, StyleSheet, TouchableOpacity, Platform, Alert, ActivityIndicator } from 'react-native';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Text, Title } from '@constants/Themed';
import DefaultScreen from '@components/Global/DefaultScreenView';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { MaterialCommunityIcons, AntDesign } from '@expo/vector-icons';
import apis, { syncPayrollToAccounting, getAccountingIntegrations, connectAccountingSoftware } from '@api/api';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';

const PayrollManagement = () => {
  const theme = themeSelector();
  const currentCompany = useSelector((state: DefaultRootState) => state.app.selectedProfile);
  const queryClient = useQueryClient();
  const currentDate = new Date();
  const [selectedMonth] = useState(String(currentDate.getMonth() + 1));
  const [selectedYear] = useState(String(currentDate.getFullYear()));
  const [showIntegrationModal, setShowIntegrationModal] = useState(false);

  const { data: payrollData, isLoading } = useQuery({
    queryKey: ['hrPayroll', currentCompany?._id, selectedMonth, selectedYear],
    queryFn: async () => {
      const response = await apis.getPayroll({ month: selectedMonth, year: selectedYear });
      return response?.data || [];
    },
    enabled: !!currentCompany?._id
  });

  const { data: accountingIntegrations } = useQuery({
    queryKey: ['accountingIntegrations', currentCompany?._id],
    queryFn: async () => {
      const response = await getAccountingIntegrations();
      return response?.data || { xero: false, sage: false };
    },
    enabled: !!currentCompany?._id
  });

  const syncMutation = useMutation({
    mutationFn: ({ payrollId, software }: { payrollId: string; software: 'xero' | 'sage' }) =>
      syncPayrollToAccounting(payrollId, software, { createInvoice: true, syncContacts: true }),
    onSuccess: (data, variables) => {
      Alert.alert('Success', `Payroll synced to ${variables.software.toUpperCase()} successfully`);
      queryClient.invalidateQueries({ queryKey: ['hrPayroll'] });
    },
    onError: (error: any) => {
      Alert.alert('Error', error.message || 'Failed to sync payroll to accounting software');
    }
  });

  const payroll = payrollData || [];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return '#10b981';
      case 'processed': return '#3b82f6';
      case 'pending': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  return (
    <DefaultScreen>
      <View style={styles.container}>
        <View style={styles.header}>
          <View style={styles.headerTop}>
            <View style={styles.headerText}>
              <Title style={{ fontSize: 28, fontWeight: '700', color: theme ? '#111827' : '#f9fafb' }}>
                Payroll Management
              </Title>
              <Text style={{ fontSize: 14, color: theme ? '#6b7280' : '#9ca3af', marginTop: 4 }}>
                {new Date(parseInt(selectedYear), parseInt(selectedMonth) - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </Text>
            </View>
            <TouchableOpacity
              style={[styles.integrationButton, { backgroundColor: theme ? '#f3f4f6' : '#374151' }]}
              onPress={() => setShowIntegrationModal(true)}
            >
              <MaterialCommunityIcons name="link-variant" size={20} color={theme ? '#3b82f6' : '#60a5fa'} />
              <Text style={[styles.integrationButtonText, { color: theme ? '#3b82f6' : '#60a5fa' }]}>
                Integrations
              </Text>
            </TouchableOpacity>
          </View>
          
          {/* Accounting Integration Status */}
          {(accountingIntegrations?.xero || accountingIntegrations?.sage) && (
            <View style={styles.integrationStatus}>
              {accountingIntegrations.xero && (
                <View style={[styles.integrationBadge, { backgroundColor: '#13B5EA20' }]}>
                  <MaterialCommunityIcons name="check-circle" size={16} color="#13B5EA" />
                  <Text style={[styles.integrationBadgeText, { color: '#13B5EA' }]}>Xero Connected</Text>
                </View>
              )}
              {accountingIntegrations.sage && (
                <View style={[styles.integrationBadge, { backgroundColor: '#00A65120' }]}>
                  <MaterialCommunityIcons name="check-circle" size={16} color="#00A651" />
                  <Text style={[styles.integrationBadgeText, { color: '#00A651' }]}>Sage Connected</Text>
                </View>
              )}
            </View>
          )}
        </View>

        {/* Integration Modal */}
        {showIntegrationModal && (
          <View style={[styles.modalOverlay, { backgroundColor: 'rgba(0,0,0,0.5)' }]}>
            <View style={[styles.modalContent, { backgroundColor: theme ? '#ffffff' : '#1f2937' }]}>
              <View style={styles.modalHeader}>
                <Title style={{ fontSize: 20, fontWeight: '700', color: theme ? '#111827' : '#f9fafb' }}>
                  Accounting Software Integration
                </Title>
                <TouchableOpacity onPress={() => setShowIntegrationModal(false)}>
                  <AntDesign name="close" size={24} color={theme ? '#111827' : '#f9fafb'} />
                </TouchableOpacity>
              </View>
              
              <View style={styles.integrationOptions}>
                <TouchableOpacity
                  style={[
                    styles.integrationOption,
                    {
                      backgroundColor: theme ? '#f3f4f6' : '#374151',
                      borderColor: accountingIntegrations?.xero ? '#13B5EA' : (theme ? '#e5e7eb' : '#4b5563')
                    }
                  ]}
                  onPress={async () => {
                    if (!accountingIntegrations?.xero) {
                      // In a real app, this would open OAuth flow
                      Alert.alert('Connect Xero', 'Redirecting to Xero authorization...');
                    }
                  }}
                >
                  <MaterialCommunityIcons 
                    name={accountingIntegrations?.xero ? "check-circle" : "circle-outline"} 
                    size={24} 
                    color={accountingIntegrations?.xero ? "#13B5EA" : (theme ? '#6b7280' : '#9ca3af')} 
                  />
                  <View style={styles.integrationOptionInfo}>
                    <Text style={[styles.integrationOptionName, { color: theme ? '#111827' : '#f9fafb' }]}>
                      Xero
                    </Text>
                    <Text style={[styles.integrationOptionDesc, { color: theme ? '#6b7280' : '#9ca3af' }]}>
                      Sync payroll data to Xero accounting
                    </Text>
                  </View>
                </TouchableOpacity>

                <TouchableOpacity
                  style={[
                    styles.integrationOption,
                    {
                      backgroundColor: theme ? '#f3f4f6' : '#374151',
                      borderColor: accountingIntegrations?.sage ? '#00A651' : (theme ? '#e5e7eb' : '#4b5563')
                    }
                  ]}
                  onPress={async () => {
                    if (!accountingIntegrations?.sage) {
                      // In a real app, this would open OAuth flow
                      Alert.alert('Connect Sage', 'Redirecting to Sage authorization...');
                    }
                  }}
                >
                  <MaterialCommunityIcons 
                    name={accountingIntegrations?.sage ? "check-circle" : "circle-outline"} 
                    size={24} 
                    color={accountingIntegrations?.sage ? "#00A651" : (theme ? '#6b7280' : '#9ca3af')} 
                  />
                  <View style={styles.integrationOptionInfo}>
                    <Text style={[styles.integrationOptionName, { color: theme ? '#111827' : '#f9fafb' }]}>
                      Sage
                    </Text>
                    <Text style={[styles.integrationOptionDesc, { color: theme ? '#6b7280' : '#9ca3af' }]}>
                      Sync payroll data to Sage accounting
                    </Text>
                  </View>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        )}

        {isLoading ? (
          <View style={styles.loadingContainer}>
            <Text>Loading...</Text>
          </View>
        ) : (
          <ScrollView showsVerticalScrollIndicator={false}>
            {payroll.map((record: any) => (
              <View
                key={record._id}
                style={[
                  styles.payrollCard,
                  {
                    backgroundColor: theme ? '#ffffff' : '#1f2937',
                    borderColor: theme ? '#e5e7eb' : '#374151'
                  }
                ]}
              >
                <View style={styles.payrollHeader}>
                  <View style={styles.payrollInfo}>
                    <Text style={[styles.payrollPeriod, { color: theme ? '#111827' : '#f9fafb' }]}>
                      {new Date(record.payPeriod).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </Text>
                    <Text style={[styles.payrollAmount, { color: theme ? '#111827' : '#f9fafb' }]}>
                      ${record.netPay?.toLocaleString() || '0.00'}
                    </Text>
                  </View>
                  <View style={[
                    styles.statusBadge,
                    { backgroundColor: `${getStatusColor(record.status)}20` }
                  ]}>
                    <Text style={[
                      styles.statusText,
                      { color: getStatusColor(record.status) }
                    ]}>
                      {record.status}
                    </Text>
                  </View>
                </View>

                <View style={styles.payrollDetails}>
                  <View style={styles.detailRow}>
                    <Text style={[styles.detailLabel, { color: theme ? '#6b7280' : '#9ca3af' }]}>
                      Base Salary:
                    </Text>
                    <Text style={[styles.detailValue, { color: theme ? '#111827' : '#f9fafb' }]}>
                      ${record.baseSalary?.toLocaleString() || '0.00'}
                    </Text>
                  </View>
                  {record.bonuses && record.bonuses.length > 0 && (
                    <View style={styles.detailRow}>
                      <Text style={[styles.detailLabel, { color: theme ? '#6b7280' : '#9ca3af' }]}>
                        Bonuses:
                      </Text>
                      <Text style={[styles.detailValue, { color: '#10b981' }]}>
                        +${record.bonuses.reduce((sum: number, b: any) => sum + (b.amount || 0), 0).toLocaleString()}
                      </Text>
                    </View>
                  )}
                  {record.deductions && record.deductions.length > 0 && (
                    <View style={styles.detailRow}>
                      <Text style={[styles.detailLabel, { color: theme ? '#6b7280' : '#9ca3af' }]}>
                        Deductions:
                      </Text>
                      <Text style={[styles.detailValue, { color: '#ef4444' }]}>
                        -${record.deductions.reduce((sum: number, d: any) => sum + (d.amount || 0), 0).toLocaleString()}
                      </Text>
                    </View>
                  )}
                </View>

                {/* Accounting Sync Actions */}
                {(accountingIntegrations?.xero || accountingIntegrations?.sage) && (
                  <View style={[styles.syncActions, { borderTopColor: theme ? '#e5e7eb' : '#374151' }]}>
                    <Text style={[styles.syncLabel, { color: theme ? '#6b7280' : '#9ca3af' }]}>
                      Sync to Accounting:
                    </Text>
                    <View style={styles.syncButtons}>
                      {accountingIntegrations?.xero && (
                        <TouchableOpacity
                          style={[styles.syncButton, { backgroundColor: '#13B5EA' }]}
                          onPress={() => {
                            Alert.alert(
                              'Sync to Xero',
                              'This will sync payroll data to Xero. Continue?',
                              [
                                { text: 'Cancel', style: 'cancel' },
                                {
                                  text: 'Sync',
                                  onPress: () => syncMutation.mutate({ payrollId: record._id, software: 'xero' })
                                }
                              ]
                            );
                          }}
                          disabled={syncMutation.isPending}
                        >
                          {syncMutation.isPending ? (
                            <ActivityIndicator size="small" color="#ffffff" />
                          ) : (
                            <>
                              <MaterialCommunityIcons name="sync" size={16} color="#ffffff" />
                              <Text style={styles.syncButtonText}>Xero</Text>
                            </>
                          )}
                        </TouchableOpacity>
                      )}
                      {accountingIntegrations?.sage && (
                        <TouchableOpacity
                          style={[styles.syncButton, { backgroundColor: '#00A651' }]}
                          onPress={() => {
                            Alert.alert(
                              'Sync to Sage',
                              'This will sync payroll data to Sage. Continue?',
                              [
                                { text: 'Cancel', style: 'cancel' },
                                {
                                  text: 'Sync',
                                  onPress: () => syncMutation.mutate({ payrollId: record._id, software: 'sage' })
                                }
                              ]
                            );
                          }}
                          disabled={syncMutation.isPending}
                        >
                          {syncMutation.isPending ? (
                            <ActivityIndicator size="small" color="#ffffff" />
                          ) : (
                            <>
                              <MaterialCommunityIcons name="sync" size={16} color="#ffffff" />
                              <Text style={styles.syncButtonText}>Sage</Text>
                            </>
                          )}
                        </TouchableOpacity>
                      )}
                    </View>
                  </View>
                )}
              </View>
            ))}
            {payroll.length === 0 && (
              <View style={styles.emptyContainer}>
                <MaterialCommunityIcons name="cash-multiple" size={48} color={theme ? '#9ca3af' : '#6b7280'} />
                <Text style={[styles.emptyText, { color: theme ? '#6b7280' : '#9ca3af' }]}>
                  No payroll records for this period
                </Text>
              </View>
            )}
          </ScrollView>
        )}
      </View>
    </DefaultScreen>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20
  },
  header: {
    marginBottom: 24
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40
  },
  payrollCard: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 12
  },
  payrollHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12
  },
  payrollInfo: {
    flex: 1
  },
  payrollPeriod: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 4
  },
  payrollAmount: {
    fontSize: 24,
    fontWeight: '700'
  },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 6
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'capitalize'
  },
  payrollDetails: {
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb'
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8
  },
  detailLabel: {
    fontSize: 14
  },
  detailValue: {
    fontSize: 14,
    fontWeight: '600'
  },
  emptyContainer: {
    padding: 40,
    alignItems: 'center'
  },
  emptyText: {
    fontSize: 16,
    marginTop: 12
  },
  headerTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start'
  },
  headerText: {
    flex: 1
  },
  integrationButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    gap: 8
  },
  integrationButtonText: {
    fontSize: 14,
    fontWeight: '600'
  },
  integrationStatus: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 12
  },
  integrationBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    gap: 6
  },
  integrationBadgeText: {
    fontSize: 12,
    fontWeight: '600'
  },
  modalOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  },
  modalContent: {
    width: '90%',
    maxWidth: 500,
    padding: 24,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20
  },
  integrationOptions: {
    gap: 12
  },
  integrationOption: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 8,
    borderWidth: 2,
    gap: 12
  },
  integrationOptionInfo: {
    flex: 1
  },
  integrationOptionName: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4
  },
  integrationOptionDesc: {
    fontSize: 14
  },
  syncActions: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1
  },
  syncLabel: {
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 8
  },
  syncButtons: {
    flexDirection: 'row',
    gap: 8
  },
  syncButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    gap: 6
  },
  syncButtonText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: '600'
  }
});

export default PayrollManagement;

